// The operating-mode key set , the one copy.
//
// This list previously existed twice: lib/server/operating-modes.ts (server-only, pulls
// in Supabase) and hooks/useBranchOperatingModes.ts ('use client', pulls in React). Two
// copies of a closed key set is the same failure mode doc 42 exists to remove, and it
// also meant nothing outside a browser or a server request could read the list at all.
//
// All mode metadata now lives in configs/billing/plans.json. This file derives from it
// so adding or changing a mode only requires a config edit.

import { ALL_OPERATING_MODE_KEYS, getModeDisplay } from '@/lib/config-system';

// ─── Core key set ────────────────────────────────────────────────────────────

export const OPERATING_MODES = ALL_OPERATING_MODE_KEYS;

export type OperatingMode = (typeof OPERATING_MODES)[number];

export type OperatingModeState = 'inactive' | 'active' | 'disabling';

/**
 * Doc 42 D1: modes whose surfaces do not exist. Modes listed here are hidden from
 * the merchant mode picker but remain in the OPERATING_MODES array for data integrity.
 * Currently all modes have surfaces, so this list is empty.
 *
 * Removing a mode from here is the *last* step of shipping its surface, not the first.
 * When all modes have surfaces, this list can be removed entirely.
 */
export const UNBUILT_OPERATING_MODES: readonly OperatingMode[] = [];

/** Modes a merchant may actually switch on today. */
export const SELECTABLE_OPERATING_MODES: readonly OperatingMode[] = OPERATING_MODES.filter(
  (mode) => !UNBUILT_OPERATING_MODES.includes(mode),
);

export function isOperatingMode(value: unknown): value is OperatingMode {
  return typeof value === 'string' && (OPERATING_MODES as readonly string[]).includes(value);
}

/**
 * Modes that are active by default when a branch has zero branch_operating_modes rows.
 * Exposed here (not server-only) so client components can import it without pulling in Supabase.
 */
export const DEFAULT_ACTIVE_MODES: readonly OperatingMode[] = ['dine_in', 'counter_pickup'];

// ─── Display metadata (derived from config) ──────────────────────────────────

/** Client-safe operating-mode labels , single source of truth. */
export const OPERATING_MODE_LABELS: Record<OperatingMode, string> = Object.fromEntries(
  OPERATING_MODES.map((key) => {
    const display = getModeDisplay(key);
    return [key, display?.customerLabel ?? display?.label ?? key];
  }),
) as Record<OperatingMode, string>;

/** Short descriptions for operating modes. */
export const OPERATING_MODE_DESCRIPTIONS: Record<OperatingMode, string> = Object.fromEntries(
  OPERATING_MODES.map((key) => {
    const display = getModeDisplay(key);
    return [key, display?.customerDescription ?? display?.description ?? ''];
  }),
) as Record<OperatingMode, string>;

/** Admin-facing (verbose) labels , used in dashboard mode-management UI. */
export const ADMIN_OPERATING_MODE_LABELS: Record<OperatingMode, string> = Object.fromEntries(
  OPERATING_MODES.map((key) => {
    const display = getModeDisplay(key);
    return [key, display?.adminLabel ?? display?.label ?? key];
  }),
) as Record<OperatingMode, string>;

/** Admin-facing (verbose) descriptions , used in dashboard mode-management UI. */
export const ADMIN_OPERATING_MODE_DESCRIPTIONS: Record<OperatingMode, string> = Object.fromEntries(
  OPERATING_MODES.map((key) => {
    const display = getModeDisplay(key);
    return [key, display?.adminDescription ?? display?.description ?? ''];
  }),
) as Record<OperatingMode, string>;
